import { redirect } from '@tanstack/react-router';

import { useAuthStore } from '@/lib/auth-store';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3200';

/**
 * A csendes refresh három kimenete (fix-kör R1 — audit T3 ugyanaz a hiba a refresh-ágon):
 * - `ok`: friss access-token, a store frissítve.
 * - `invalid`: a refresh-token GENUINE érvénytelen/visszavont (401) — a session valóban véget ért.
 * - `unavailable`: az Odin átmenetileg nem elérhető (503), a rate-limit elkapta (429, C1 — NAT mögötti
 *   megosztott IP + sok fül könnyen összead 10/perc fölé), vagy maga a fetch dobott — a refresh-token
 *   valószínűleg még érvényes, csak most nem tudtuk beváltani. A hívó NEM kezelheti ezt kijelentkezésként.
 *
 * FAIL-SAFE leképezés: csak a GENUINE 401 → `invalid`. Minden más, fel nem ismert státusz (bármi, ami
 * nem 200 és nem 401) → `unavailable` — új/ismeretlen szerver-válasz sose jelentsen csendben
 * kijelentkezést.
 */
export type RefreshResult = 'ok' | 'invalid' | 'unavailable';

// Egyetlen futó refresh (dedup): párhuzamos 401-ek / route-váltások EGY refresh-hívást osztanak.
let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Csendes access-token megújítás a httpOnly refresh-cookie-ból (`POST /auth/refresh`, credentials: include).
 * Lásd `RefreshResult` a három kimenetért — a hívó dönt, mit kezd `unavailable`-lel (jellemzően: NE
 * jelentkeztesse ki a felhasználót, hagyja az eredeti hibát felszínre kerülni).
 */
export function tryRefresh(): Promise<RefreshResult> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      // C1: csak a GENUINE 401 jelent érvénytelen session-t. A 429 (rate-limit) és a 503 (Odin-kiesés)
      // egyaránt átmeneti — és a fail-safe alapállás szerint MINDEN egyéb, fel nem ismert státusz is
      // ide tartozik, sose az `invalid` ágra (retry-loopot szándékosan nem építünk erre — a hívó dönt).
      if (res.status === 401) return 'invalid';
      if (!res.ok) return 'unavailable';
      // A 200-as, de token NÉLKÜLI válasz nem a session vége: ilyet a szerződés szerint a szerver
      // nem ad, tehát ha mégis megtörténik (proxy csonkolta a törzset, félbeszakadt válasz), az a
      // csatorna hibája. A fail-safe alapállás itt is `unavailable` — a felhasználót nem
      // jelentkeztetjük ki olyan ok miatt, amiről a refresh-token érvényességére semmit nem tudunk.
      // (Egy JSON-parse hiba ugyanezen az elven a lenti `catch`-be esik, szintén `unavailable`.)
      const body = (await res.json()) as { accessToken?: string };
      if (!body.accessToken) return 'unavailable';
      useAuthStore.getState().setToken(body.accessToken);
      return 'ok';
    } catch {
      // Hálózati hiba magán a refresh-híváson — ugyanúgy átmeneti, nem "a token érvénytelen".
      return 'unavailable';
    } finally {
      // A dedup-slotot a következő mikrotaszkban engedjük el (a most várakozók még ezt az eredményt kapják).
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/**
 * Route-guard (beforeLoad): ha nincs memória-token (pl. reload után), előbb csendes refresh-t próbál;
 * ha az nem ad tokent (érvénytelen VAGY átmenetileg nem elérhető) → redirect a loginra — kezdő
 * betöltésnél nincs mit megőrizni, ha nincs érvényes access-token a kézben.
 *
 * `redirectTo` a hívó route aktuális helye (`location.href`, audit T14) — a login ezt kapja
 * `?redirect=` search-paramként, hogy sikeres belépés után ide, ne a kezdőlapra navigáljon.
 */
export async function requireAuth(redirectTo?: string): Promise<void> {
  if (useAuthStore.getState().token) return;
  const result = await tryRefresh();
  if (result === 'ok') return;
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- a TanStack Router redirect-idiómája
  throw redirect({ to: '/login', search: redirectTo ? { redirect: redirectTo } : {} });
}

/**
 * Open-redirect-védelem (audit T14): a login utáni cél csak app-belüli útvonal lehet.
 * A `redirect` search-param a URL-ből jön, tehát bárki tetszőleges értéket rakhat bele — enélkül a
 * védelem nélkül egy külső abszolút URL (`https://gonosz.hu`) a belépés UTÁN vinné el a
 * felhasználót az oldalról.
 *
 * A puszta `startsWith('/') && !startsWith('//')` ellenőrzés (a brief baseline-ja) nem elég: a
 * böngészők a speciális sémák (http/https/ws/wss/ftp/file) relatív feloldásánál a backslash-t
 * forward slash-ként kezelik, tehát pl. `/\evil.com` vagy `/\/evil.com` ugyanúgy protokoll-relatív
 * külső célra normalizálódhat, mint a nyílt `//evil.com` — ezért a második karakter se lehet `/`
 * VAGY `\`.
 */
export function isSafeRedirectTarget(target: string | undefined): target is string {
  return !!target && target.startsWith('/') && !/^\/[/\\]/.test(target);
}

/** Teljes kijelentkezés: refresh-token revoke az Odin-ban (a cookie-n át) + a memória-token törlése. */
export async function logoutEverywhere(): Promise<void> {
  await fetch(`${baseUrl}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(
    () => undefined,
  );
  useAuthStore.getState().logout();
}
